<template>
   <b-modal
    ref="modal"
    size="lg"
    title="Tạo đơn vận chuyển"
    no-close-on-backdrop
    hide-footer
    @hidden="onHidden"
    > <!-- Step indicators -->
    <div class="d-flex justify-content-center mb-4">

      <div
        v-for="(step, i) in steps"
        :key="i"
        class="d-flex align-items-center"
      >

        <div
          class="step-circle"
          :class="{
            'step-active': currentStep === i,
            'step-done': currentStep > i,
          }"
        >
           <i
            v-if="currentStep > i"
            class="fas fa-check"
            style="font-size: 12px"
          /> <span v-else>{{ i + 1 }}</span
          >
        </div>
         <span
          class="mx-2"
          :class="currentStep === i ? 'font-weight-bold text-primary' : 'text-muted'"
          style="font-size: 13px"
          >{{ step }}</span
        >
        <div
          v-if="i < steps.length - 1"
          class="step-divider"
        />

      </div>

    </div>
     <!-- Step 1: Chọn nhà vận chuyển -->
    <div v-if="currentStep === 0">

      <p class="text-muted mb-3">Chọn nhà vận chuyển để giao đơn hàng này:</p>
       <b-overlay
        :show="loadingCarriers"
        rounded="sm"
        > <b-row
          > <b-col
            v-for="carrier in activeCarriers"
            :key="carrier.carrier"
            cols="6"
            class="mb-3"
            >
            <div
              class="carrier-card border rounded p-3 cursor-pointer"
              :class="{ 'carrier-selected': selectedCarrier === carrier.carrier }"
              @click="selectCarrier(carrier)"
            >

              <div class="d-flex align-items-center">

                <div class="carrier-logo mr-3">
                   <span
                    v-if="carrier.carrier === 'viettelpost'"
                    class="text-danger font-weight-bold"
                    >VTP</span
                  > <span
                    v-else
                    class="text-warning font-weight-bold"
                    >NT</span
                  >
                </div>

                <div>

                  <div class="font-weight-bold">{{ carrierLabel(carrier.carrier) }}</div>

                  <div
                    v-if="carrier.companyName"
                    class="text-muted"
                    style="font-size: 11px"
                  >
                    {{ carrier.companyName }}
                  </div>

                </div>

              </div>

            </div>
             </b-col
          > </b-row
        >
        <div
          v-if="!loadingCarriers && activeCarriers.length === 0"
          class="text-center py-3 text-muted"
        >
           Chưa cấu hình nhà vận chuyển nào. Vào <span
            class="text-primary cursor-pointer"
            @click="goToSettings"
            >Cài đặt → Vận chuyển</span
          > để thêm.
        </div>
         </b-overlay
      > <!-- Hiển thị sau khi chọn NVC -->
      <div
        v-if="selectedCarrier"
        class="mt-3"
      >
         <!-- Địa chỉ gửi & nhận -->
        <div class="addr-summary border rounded p-3 mb-3 bg-light">
           <b-row
            > <b-col cols="6"
              >
              <div class="d-flex align-items-start">
                 <i
                  class="fas fa-store text-primary mt-1 mr-2"
                  style="min-width: 14px"
                ></i
                >
                <div class="flex-grow-1">

                  <div class="d-flex align-items-center justify-content-between mb-1">

                    <div class="addr-label">Người gửi</div>
                     <b-button
                      variant="link"
                      size="sm"
                      class="p-0 text-primary"
                      style="font-size: 11px; line-height: 1"
                      @click="openAddSenderModal"
                      > <i class="fas fa-plus-circle mr-1"></i>Thêm </b-button
                    >
                  </div>
                   <AutoSuggest
                    :model.sync="senderSearchText"
                    :suggestions="filteredSenderAddresses"
                    placeholder="tên hoặc số điện thoại"
                    :loading="loadingSenderAddresses"
                    show-clear
                    @change="onSenderInputChange"
                    @select="onSenderSelect"
                    > <template #custom="{ suggestion }"
                      >
                      <div class="suggestion-item">

                        <div class="font-weight-bold">
                           {{ suggestion.item.name }} - {{ suggestion.item.phone }}
                        </div>

                        <div
                          v-if="suggestion.item.provinceName"
                          class="text-muted"
                          style="font-size: 12px"
                        >
                           {{
                            [
                              suggestion.item.address,
                              suggestion.item.wardName,
                              suggestion.item.provinceName,
                            ]
                              .filter(Boolean)
                              .join(', ')
                          }}
                        </div>

                      </div>
                       </template
                    > </AutoSuggest
                  >
                  <div
                    v-if="selectedSenderAddress"
                    class="mt-1"
                  >

                    <div class="addr-detail">{{ selectedSenderAddress.phone }}</div>

                    <div
                      v-if="selectedSenderAddress.address"
                      class="addr-detail"
                    >
                       {{
                        [
                          selectedSenderAddress.address,
                          selectedSenderAddress.wardName,
                          selectedSenderAddress.provinceName,
                        ]
                          .filter(Boolean)
                          .join(', ')
                      }}
                    </div>

                  </div>

                </div>

              </div>
               </b-col
            > <b-col
              cols="6"
              class="border-left"
              >
              <div class="d-flex align-items-start">
                 <i
                  class="fas fa-map-marker-alt text-danger mt-1 mr-2"
                  style="min-width: 14px"
                ></i
                >
                <div>

                  <div class="addr-label">Người nhận</div>

                  <div class="addr-name">{{ orderInfo.customerName || '—' }}</div>

                  <div
                    v-if="orderInfo.customerMobile"
                    class="addr-detail"
                  >
                    {{ orderInfo.customerMobile }}
                  </div>

                  <div
                    v-if="receiverFullAddress"
                    class="addr-detail"
                  >
                    {{ receiverFullAddress }}
                  </div>

                </div>

              </div>
               </b-col
            > </b-row
          >
        </div>
         <!-- NT Logistics: địa chỉ giao hàng người nhận -->
        <div
          v-if="selectedCarrier === 'nhattin'"
          class="border rounded p-3 bg-light"
        >

          <div class="d-flex align-items-center mb-2">
             <i class="fas fa-map-marker-alt text-danger mr-2"></i> <strong style="font-size: 13px"
              >Địa chỉ giao hàng</strong
            > <small class="text-muted ml-2">— bắt buộc để tính giá cước chính xác</small>
          </div>
           <b-row
            > <b-col cols="6"
              > <label>Tỉnh / Thành phố <span class="text-danger">*</span></label
              > <b-form-group class="mb-2"
                > <AutoSuggest
                  :model.sync="ntAddr.provinceName"
                  :suggestions="ntFilteredProvinces"
                  suggestion-name="province_name"
                  placeholder="tỉnh/thành phố"
                  :loading="loadingNtProvinces"
                  show-clear
                  @change="onNtProvinceInputChange"
                  @select="onNtProvinceSelect"
                /> </b-form-group
              > </b-col
            > <b-col cols="6"
              > <label>Phường / Xã <span class="text-danger">*</span></label
              > <b-form-group class="mb-2"
                > <AutoSuggest
                  :model.sync="ntAddr.wardName"
                  :suggestions="ntFilteredWards"
                  suggestion-name="ward_name"
                  placeholder="phường/xã"
                  :loading="loadingNtWards"
                  :disabled="!ntAddr.provinceId"
                  show-clear
                  @change="onNtWardInputChange"
                  @select="onNtWardSelect"
                /> </b-form-group
              > </b-col
            > </b-row
          >
        </div>

      </div>

      <div class="d-flex justify-content-end mt-3">
         <b-button
          variant="primary"
          :disabled="
            !selectedCarrier ||
            (selectedCarrier === 'nhattin' && (!ntAddr.provinceId || !ntAddr.wardCode))
          "
          @click="goToStep(1)"
          > Tiếp theo <i class="fas fa-arrow-right ml-1"></i> </b-button
        >
      </div>

    </div>
     <!-- Step 2: Chọn dịch vụ -->
    <div v-if="currentStep === 1">

      <p class="text-muted mb-3">
         Chọn dịch vụ vận chuyển của <strong>{{ carrierLabel(selectedCarrier) }}</strong
        >:
      </p>
       <b-overlay
        :show="loadingServices"
        rounded="sm"
        >
        <div
          v-if="services.length === 0 && !loadingServices"
          class="text-center text-muted py-3"
        >
           Không lấy được danh sách dịch vụ. Kiểm tra lại cấu hình.
        </div>

        <div
          v-for="svc in services"
          :key="svc.serviceCode || svc.serviceId"
          class="service-row border rounded p-3 mb-2 cursor-pointer"
          :class="{ 'service-selected': isServiceSelected(svc) }"
          @click="selectService(svc)"
        >

          <div class="d-flex justify-content-between align-items-center">

            <div>
               <span class="font-weight-bold">{{ svc.serviceName }}</span
              > <span
                v-if="svc.serviceCode"
                class="text-muted ml-2"
                >({{ svc.serviceCode }})</span
              >
            </div>

            <div class="text-right">

              <div class="text-primary font-weight-bold">
                {{ formatCurrency(svc.price || svc.totalFee) }}
              </div>
               <small
                v-if="svc.expectedAt"
                class="text-muted"
                > Dự kiến: {{ formatDate(svc.expectedAt) }} </small
              >
            </div>

          </div>
           <!-- VTP: dịch vụ cộng thêm -->
          <div
            v-if="
              selectedCarrier === 'viettelpost' &&
              isServiceSelected(svc) &&
              svc.extraServices &&
              svc.extraServices.length
            "
            class="mt-2 pt-2 border-top"
            @click.stop
          >
            <small class="text-muted d-block mb-1">Dịch vụ cộng thêm (tùy chọn):</small>
            <b-form-input
              v-model="extraServiceSearch"
              placeholder="Tìm dịch vụ..."
              size="sm"
              class="mb-2"
            />
            <div
              v-if="filteredExtraServices(svc).length === 0"
              class="text-muted"
              style="font-size: 12px"
            >
              Không tìm thấy dịch vụ phù hợp.
            </div>
            <b-form-checkbox-group
              v-else
              v-model="selectedExtraServices"
              :options="
                filteredExtraServices(svc).map((e) => ({
                  text: `${e.name} (+${formatCurrency(e.price)})`,
                  value: e.code,
                }))
              "
              stacked
              size="sm"
              @change="extraServiceSearch = ''"
            />
          </div>

        </div>
         </b-overlay
      >
      <div class="d-flex justify-content-between mt-3">
         <b-button
          variant="outline-secondary"
          @click="goToStep(0)"
          > <i class="fas fa-arrow-left mr-1"></i> Quay lại </b-button
        > <b-button
          variant="primary"
          :disabled="!selectedService"
          @click="goToStep(2)"
          > Tiếp theo <i class="fas fa-arrow-right ml-1"></i> </b-button
        >
      </div>

    </div>
     <!-- Step 3: Nhập thông tin -->
    <div v-if="currentStep === 2">
       <b-form
        > <b-row
          > <b-col cols="6"
            > <label>Trọng lượng (gram) <span class="text-danger">*</span></label
            > <b-form-group
              > <b-form-input
                v-model.number="form.productWeight"
                type="number"
                placeholder="VD: 500"
                :state="form.productWeight > 0 ? true : null"
              /> </b-form-group
            > </b-col
          > <b-col cols="6"
            > <b-form-group label="Tiền thu hộ COD (₫)"
              > <BInputNumber
                v-model="form.codAmount"
                :placeholder="String(orderInfo.totalAmount || 0)"
              /> <b-form-text>Mặc định: tổng giá đơn hàng</b-form-text> </b-form-group
            > </b-col
          > </b-row
        > <b-row v-if="selectedCarrier === 'nhattin' || selectedCarrier === 'viettelpost'"
          > <b-col cols="6"
            > <b-form-group label="Giá trị hàng hoá (₫)"
              > <BInputNumber
                v-model="form.cargoValue"
                v-if="selectedCarrier === 'nhattin'"
              /> <BInputNumber
                v-model="form.productPrice"
                v-if="selectedCarrier === 'viettelpost'"
              /> <b-form-text>Mặc định: tổng giá trị đơn hàng</b-form-text> </b-form-group
            > </b-col
          > </b-row
        > <b-row
          > <b-col cols="4"
            > <label>Hình thức thanh toán <span class="text-danger">*</span></label
            > <b-form-group
              > <b-form-select
                v-model="form.paymentMethod"
                :options="paymentOptions"
                size="sm"
              /> </b-form-group
            > </b-col
          > <b-col cols="8"
            > <b-form-group label="Kích thước: Dài - rộng - cao (cm)"
              > <b-input-group
                > <BInputNumber
                  v-model="form.productLength"
                  placeholder="Dài"
                /> <BInputNumber
                  v-model="form.productWidth"
                  placeholder="Rộng"
                /> <BInputNumber
                  v-model="form.productHeight"
                  placeholder="Cao"
                /> </b-input-group
              > </b-form-group
            > </b-col
          > </b-row
        > <b-form-group
          v-if="selectedCarrier === 'nhattin'"
          label="Mô tả hàng hoá"
          > <b-form-input
            v-model="form.cargoContent"
            placeholder="VD: Điện thoại, phụ kiện..."
          /> </b-form-group
        > <b-form-group label="Ghi chú giao hàng"
          > <b-form-input
            v-model="form.note"
            placeholder="VD: Giao giờ hành chính, cho xem hàng..."
          /> </b-form-group
        > <!-- Tóm tắt -->
        <div class="summary-box border rounded p-3 bg-light">

          <div class="d-flex justify-content-between mb-1">
             <span class="text-muted">Nhà vận chuyển:</span> <strong>{{
              carrierLabel(selectedCarrier)
            }}</strong
            >
          </div>

          <div
            v-if="selectedService"
            class="d-flex justify-content-between mb-1"
          >
             <span class="text-muted">Dịch vụ:</span> <strong>{{
              selectedService.serviceName
            }}</strong
            >
          </div>

          <div
            v-if="selectedService"
            class="d-flex justify-content-between"
          >
             <span class="text-muted">Cước phí dự tính:</span> <strong class="text-primary">{{
              formatCurrency(selectedService.price || selectedService.totalFee)
            }}</strong
            >
          </div>

        </div>
         </b-form
      >
      <div class="d-flex justify-content-between mt-3">
         <b-button
          variant="outline-secondary"
          @click="goToStep(1)"
          > <i class="fas fa-arrow-left mr-1"></i> Quay lại </b-button
        > <b-button
          variant="success"
          @click="submit"
          :disabled="isSubmitting || !form.productWeight"
          > <b-spinner
            v-if="isSubmitting"
            small
            class="mr-1"
          /> <i
            v-else
            class="fas fa-check mr-1"
          ></i
          > {{ isSubmitting ? 'Đang tạo...' : 'Tạo đơn vận chuyển' }} </b-button
        >
      </div>

    </div>
     <!-- Modal thêm địa chỉ người gửi --> <SenderAddressModal
      ref="senderAddressModal"
      :store-id="orderInfo.storeId || null"
      @saved="onSenderAddressSaved"
    /> <!-- Step 4: Kết quả -->
    <div
      v-if="currentStep === 3 && createdResult"
      class="text-center py-3"
    >

      <div class="mb-3">
         <i
          class="fas fa-check-circle text-success"
          style="font-size: 48px"
        ></i
        >
      </div>

      <h5 class="font-weight-bold text-success mb-3">Tạo đơn vận chuyển thành công!</h5>

      <div class="border rounded p-3 mb-3 text-left">
         <b-row
          > <b-col cols="6"
            >
            <div class="mb-2">
               <small class="text-muted d-block">Nhà vận chuyển</small> <strong>{{
                carrierLabel(selectedCarrier)
              }}</strong
              >
            </div>

            <div class="mb-2">
               <small class="text-muted d-block">Mã vận đơn</small> <strong
                class="text-primary"
                style="font-size: 18px"
                >{{ createdResult.trackingCode }}</strong
              > <b-button
                size="sm"
                variant="link"
                class="p-0 ml-1"
                @click="copyCode(createdResult.trackingCode)"
                > <i class="far fa-copy"></i> </b-button
              >
            </div>
             </b-col
          > <b-col cols="6"
            >
            <div
              v-if="createdResult.totalFee"
              class="mb-2"
            >
               <small class="text-muted d-block">Cước phí</small> <strong class="text-primary">{{
                formatCurrency(createdResult.totalFee)
              }}</strong
              >
            </div>

            <div
              v-if="createdResult.expectedDeliveryAt"
              class="mb-2"
            >
               <small class="text-muted d-block">Dự kiến giao</small> <strong>{{
                formatDate(createdResult.expectedDeliveryAt)
              }}</strong
              >
            </div>

            <div
              v-if="!createdResult.expectedDeliveryAt"
              class="mb-2"
            >
               <small class="text-muted d-block">Thời gian giao</small> <strong
                >48 giờ (dự kiến)</strong
              >
            </div>
             </b-col
          > </b-row
        >
      </div>
       <b-button
        variant="primary"
        @click="$refs.modal.hide()"
        > <i class="fas fa-check mr-1"></i> Đóng </b-button
      >
    </div>
     </b-modal
  >
</template>

<script>
  import { RepositoryFactory } from '@/repositories/RepositoryFactory'
  import { makeToastFaile, removeAccents } from '@/utils/common'
  import { VTP_PAYMENT_METHOD, NT_PAYMENT_METHOD } from '@/utils/enum'
  import { VTP_PAYMENT_METHOD_NAME, NT_PAYMENT_METHOD_NAME } from '@/utils/enum-name'
  import BInputNumber from '@/view/base/bootstrap/BInputNumber.vue'
  import SenderAddressModal from './SenderAddressModal.vue'
  import AutoSuggest from '@/view/base/auto-sugguest/AutoSuggest.vue'

  const ShippingRepo = RepositoryFactory.get('shipping')

  export default {
    name: 'ShippingOrderModal',
    components: { BInputNumber, SenderAddressModal, AutoSuggest },
    props: {
      orderId: { type: Number, required: true },
      orderInfo: { type: Object, default: () => ({}) },
    },
    data() {
      return {
        currentStep: 0,
        steps: ['Chọn nhà vận chuyển', 'Chọn dịch vụ', 'Thông tin gói hàng', 'Kết quả'],

        // Step 1
        allCarriers: [],
        loadingCarriers: false,
        selectedCarrier: null,

        // Sender address picker
        senderAddresses: [],
        loadingSenderAddresses: false,
        selectedSenderAddressId: null,
        senderSearchText: '',

        // Step 2
        services: [],
        loadingServices: false,
        selectedService: null,
        selectedExtraServices: [],
        extraServiceSearch: '',

        // NT address picker
        ntProvinces: [],
        ntWards: [],
        loadingNtProvinces: false,
        loadingNtWards: false,
        ntAddr: { provinceId: null, provinceName: '', wardCode: null, wardName: '' },

        // Step 3
        form: {
          productWeight: 500,
          codAmount: null,
          cargoValue: null,
          productPrice: null,
          paymentMethod: null,
          productLength: null,
          productWidth: null,
          productHeight: null,
          note: '',
          cargoContent: '',
        },
        isSubmitting: false,
        createdResult: null,
      }
    },
    computed: {
      activeCarriers() {
        return this.allCarriers.filter((c) => c.isActive)
      },
      filteredSenderAddresses() {
        const q = (this.senderSearchText || '').toLowerCase().trim()
        if (!q) return this.senderAddresses
        return this.senderAddresses.filter(
          (a) =>
            a.name.toLowerCase().includes(q) ||
            (a.phone || '').includes(q) ||
            (a.provinceName || '').toLowerCase().includes(q),
        )
      },
      selectedSenderAddress() {
        if (!this.selectedSenderAddressId) return null
        return this.senderAddresses.find((a) => a.id === this.selectedSenderAddressId) || null
      },
      receiverFullAddress() {
        return [
          this.orderInfo.customerAddress,
          this.orderInfo.customerWard,
          this.orderInfo.customerDistrict,
          this.orderInfo.customerCity,
        ]
          .filter(Boolean)
          .join(', ')
      },
      ntFilteredProvinces() {
        const q = (this.ntAddr.provinceName || '').toLowerCase().trim()
        if (!q) return this.ntProvinces
        return this.ntProvinces.filter((p) => p.province_name.toLowerCase().includes(q))
      },
      ntFilteredWards() {
        const q = (this.ntAddr.wardName || '').toLowerCase().trim()
        if (!q) return this.ntWards
        return this.ntWards.filter((w) => w.ward_name.toLowerCase().includes(q))
      },
      paymentOptions() {
        if (this.selectedCarrier === 'viettelpost') {
          return Object.values(VTP_PAYMENT_METHOD).map((v) => ({
            value: v,
            text: VTP_PAYMENT_METHOD_NAME[v],
          }))
        }
        return Object.values(NT_PAYMENT_METHOD).map((v) => ({
          value: v,
          text: NT_PAYMENT_METHOD_NAME[v],
        }))
      },
    },
    watch: {
      'form.codAmount'(val) {
        if (this.selectedCarrier) {
          this.form.paymentMethod = this.applyDefaultPaymentMethod(Number(val) > 0)
        }
      },
    },
    methods: {
      applyDefaultPaymentMethod(hasCod = true) {
        if (this.selectedCarrier === 'viettelpost') {
          return hasCod ? VTP_PAYMENT_METHOD.COLLECT_GOODS_ONLY : VTP_PAYMENT_METHOD.NO_COLLECTION
        }
        if (this.selectedCarrier === 'nhattin') {
          return hasCod ? NT_PAYMENT_METHOD.RECEIVER_PAY_NOW : NT_PAYMENT_METHOD.SENDER_PAY_LATER
        }
      },
      open() {
        this.reset()
        this.$refs.modal.show()
        this.fetchCarriers()
      },

      onHidden() {
        this.reset()
      },

      reset() {
        this.currentStep = 0
        this.selectedCarrier = null
        this.senderAddresses = []
        this.selectedSenderAddressId = null
        this.senderSearchText = ''
        this.services = []
        this.selectedService = null
        this.selectedExtraServices = []
        this.extraServiceSearch = ''
        this.createdResult = null
        this.ntProvinces = []
        this.ntWards = []
        this.ntAddr = { provinceId: null, provinceName: '', wardCode: null, wardName: '' }
        this.form = {
          productWeight: 500,
          codAmount: this.orderInfo?.totalAmount || null,
          cargoValue: this.orderInfo?.totalAmount || null,
          productPrice: this.orderInfo?.totalAmount || null,
          paymentMethod: null,
          productLength: null,
          productWidth: null,
          productHeight: null,
          note: '',
          cargoContent: '',
        }
      },

      async fetchSenderAddresses(carrier) {
        this.loadingSenderAddresses = true
        try {
          const params = { carrier };
          if (this.orderInfo?.storeId) params.storeId = this.orderInfo.storeId
          this.senderAddresses = await ShippingRepo.getSenderAddresses(params)
          const def = this.senderAddresses.find((a) => a.isDefault)
          if (def) {
            this.selectedSenderAddressId = def.id
            this.senderSearchText = def.name
          }
        } catch {
          this.senderAddresses = []
        } finally {
          this.loadingSenderAddresses = false
        }
      },

      onSenderInputChange(text) {
        if (!text) {
          this.selectedSenderAddressId = null
        }
      },

      onSenderSelect(option) {
        if (!option?.item) return
        this.selectedSenderAddressId = option.item.id
        this.senderSearchText = option.item.name
      },

      openAddSenderModal() {
        this.$refs.senderAddressModal.open(null, this.selectedCarrier)
      },

      async onSenderAddressSaved(newAddr) {
        await this.fetchSenderAddresses(this.selectedCarrier)
        this.selectedSenderAddressId = newAddr.id
        this.senderSearchText = newAddr.name
      },

      async fetchCarriers() {
        this.loadingCarriers = true
        try {
          this.allCarriers = await ShippingRepo.getCarriers()
        } catch {
          this.allCarriers = []
        } finally {
          this.loadingCarriers = false
        }
      },

      selectCarrier(carrier) {
        this.selectedCarrier = carrier.carrier
        this.selectedSenderAddressId = null
        this.senderSearchText = ''
        this.ntAddr = { provinceId: null, provinceName: '', wardCode: null, wardName: '' }
        this.ntWards = []
        this.fetchSenderAddresses(carrier.carrier)
        if (carrier.carrier === 'nhattin' && !this.ntProvinces.length) {
          this.fetchNtProvinces()
        }
      },

      isServiceSelected(svc) {
        const id = svc.serviceCode || svc.serviceId
        const selId = this.selectedService?.serviceCode || this.selectedService?.serviceId
        return id === selId
      },

      selectService(svc) {
        this.selectedService = svc
        this.selectedExtraServices = []
        this.extraServiceSearch = ''
      },

      filteredExtraServices(svc) {
        const q = removeAccents((this.extraServiceSearch || '').toLowerCase().trim())
        if (q) {
          return svc.extraServices.filter((e) => removeAccents(e.name.toLowerCase()).includes(q))
        }
        return [...svc.extraServices].sort((a, b) => {
          const aChecked = this.selectedExtraServices.includes(a.code)
          const bChecked = this.selectedExtraServices.includes(b.code)
          if (aChecked === bChecked) return 0
          return aChecked ? -1 : 1
        })
      },

      async goToStep(step) {
        if (step === 1 && this.currentStep === 0) {
          await this.fetchServices()
        }
        if (step === 2 && this.form.paymentMethod === null) {
          this.form.paymentMethod = this.applyDefaultPaymentMethod(Number(this.form.codAmount) > 0)
        }
        this.currentStep = step
      },

      async fetchNtProvinces() {
        this.loadingNtProvinces = true
        try {
          this.ntProvinces = await ShippingRepo.getNtProvinces()
          this._autoResolveReceiverNt()
        } catch {
          this.ntProvinces = []
        } finally {
          this.loadingNtProvinces = false
        }
      },

      _normalizeGeoName(text) {
        if (!text) return ''
        return text
          .replace(/^(Thành phố|Thị xã|Thị trấn|Tỉnh|Quận|Huyện|Phường|Xã)\s+/i, '')
          .toLowerCase()
          .trim()
      },

      async _autoResolveReceiverNt() {
        const cityText = this.orderInfo?.customerCity
        if (!cityText || !this.ntProvinces.length) return

        const normCity = this._normalizeGeoName(cityText)
        const province = this.ntProvinces.find(
          (p) => this._normalizeGeoName(p.province_name) === normCity,
        )
        if (!province) return

        this.ntAddr.provinceId = province.id
        this.ntAddr.provinceName = province.province_name
        this.ntAddr.wardCode = null
        this.ntAddr.wardName = ''

        const wardText = this.orderInfo?.customerWard
        if (!wardText) return

        await this._loadNtWards(province.id)

        const normWard = this._normalizeGeoName(wardText)
        const ward = this.ntWards.find((w) => this._normalizeGeoName(w.ward_name) === normWard)
        if (ward) {
          this.ntAddr.wardCode = ward.id
          this.ntAddr.wardName = ward.ward_name
        }
      },

      onNtProvinceInputChange(text) {
        if (!text) {
          this.ntAddr.provinceId = null
          this.ntAddr.wardCode = null
          this.ntAddr.wardName = ''
          this.ntWards = []
        }
      },

      onNtProvinceSelect(option) {
        if (!option?.item) return
        const item = option.item
        this.ntAddr.provinceId = item.id
        this.ntAddr.provinceName = item.province_name
        this.ntAddr.wardCode = null
        this.ntAddr.wardName = ''
        this.ntWards = []
        this._loadNtWards(item.id)
      },

      onNtWardInputChange(text) {
        if (!text) {
          this.ntAddr.wardCode = null
        }
      },

      onNtWardSelect(option) {
        if (!option?.item) return
        this.ntAddr.wardCode = option.item.id
        this.ntAddr.wardName = option.item.ward_name
      },

      async _loadNtWards(provinceId) {
        this.loadingNtWards = true
        try {
          this.ntWards = await ShippingRepo.getNtWards(provinceId)
        } catch {
          this.ntWards = []
        } finally {
          this.loadingNtWards = false
        }
      },

      async fetchServices() {
        this.loadingServices = true
        this.services = []
        this.selectedService = null
        try {
          const params = {
            carrier: this.selectedCarrier,
            productWeight: this.form.productWeight,
            codAmount: this.form.codAmount ?? this.orderInfo?.totalAmount ?? 0,
            ...(this.selectedSenderAddressId && { senderAddressId: this.selectedSenderAddressId }),
            ...(this.selectedCarrier === 'nhattin' && {
              receiverProvinceCode: this.ntAddr.provinceId,
              receiverWardCode: this.ntAddr.wardCode,
            }),
          }
          this.services = await ShippingRepo.getAvailableServices(this.orderId, params)
        } catch {
          this.services = []
          makeToastFaile('Không lấy được danh sách dịch vụ')
        } finally {
          this.loadingServices = false
        }
      },

      async submit() {
        if (!this.form.productWeight || this.form.productWeight <= 0) {
          makeToastFaile('Vui lòng nhập trọng lượng')
          return
        }

        this.isSubmitting = true
        try {
          const payload = {
            carrier: this.selectedCarrier,
            ...(this.selectedSenderAddressId && { senderAddressId: this.selectedSenderAddressId }),
            ...(this.selectedCarrier === 'viettelpost'
              ? {
                  serviceCode: this.selectedService.serviceCode,
                  serviceAddCode: this.selectedExtraServices.join(',') || undefined,
                  paymentMethod: this.form.paymentMethod,
                  productPrice: this.form.productPrice ?? this.orderInfo?.totalAmount ?? 0,
                }
              : {
                  serviceId: this.selectedService.serviceId,
                  paymentMethodId: this.form.paymentMethod,
                  cargoTypeId: 2,
                  weight: (this.form.productWeight / 1000).toFixed(3),
                  receiverProvinceCode: this.ntAddr.provinceId,
                  receiverWardCode: this.ntAddr.wardCode,
                  cargoValue: this.form.cargoValue ?? this.orderInfo?.totalAmount ?? 0,
                  cargoContent: this.form.cargoContent || undefined,
                }),
            codAmount: this.form.codAmount ?? this.orderInfo?.totalAmount ?? 0,
            productWeight: this.form.productWeight,
            productLength: this.form.productLength || undefined,
            productWidth: this.form.productWidth || undefined,
            productHeight: this.form.productHeight || undefined,
            note: this.form.note || undefined,
          }

          const result = await ShippingRepo.createShipping(this.orderId, payload)
          this.createdResult = result
          this.currentStep = 3
          this.$emit('shipping-created', result)
        } catch (err) {
          makeToastFaile(err?.response?.data?.message || 'Tạo đơn vận chuyển thất bại')
        } finally {
          this.isSubmitting = false
        }
      },

      copyCode(code) {
        navigator.clipboard?.writeText(code)
      },

      carrierLabel(carrier) {
        return { viettelpost: 'ViettelPost', nhattin: 'Nhất Tín Logistics' }[carrier] || carrier
      },

      goToSettings() {
        this.$refs.modal.hide()
        this.$router.push('/settings/shipping-carriers')
      },

      formatCurrency(value) {
        if (!value) return '0 ₫'
        return new Intl.NumberFormat('vi-VN', { style: 'currency', currency: 'VND' }).format(value)
      },

      formatDate(date) {
        if (!date) return ''
        return new Date(date).toLocaleDateString('vi-VN')
      },
    },
  }
</script>

<style scoped>
  .step-circle {
  width: 28px;
  height: 28px;
  border-radius: 50%;
  border: 2px solid #ccc;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 12px;
  font-weight: bold;
  color: #aaa;
  background: #fff;
  flex-shrink: 0;
}
.step-active {
  border-color: #007bff;
  color: #007bff;
}
.step-done {
  border-color: #28a745;
  background: #28a745;
  color: #fff;
}
.step-divider {
  width: 40px;
  height: 2px;
  background: #e0e0e0;
  margin: 0 4px;
}
.cursor-pointer {
  cursor: pointer;
}
.carrier-card:hover {
  border-color: #007bff !important;
  background: #f0f7ff;
}
.carrier-selected {
  border-color: #007bff !important;
  background: #e8f4ff;
}
.service-row:hover {
  background: #f8f9fa;
}
.service-selected {
  border-color: #007bff !important;
  background: #e8f4ff;
}
.carrier-logo {
  width: 44px;
  height: 44px;
  border-radius: 8px;
  background: #f5f5f5;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 11px;
}
.addr-summary {
  font-size: 13px;
}
.addr-label {
  font-size: 10px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  color: #6c757d;
  margin-bottom: 2px;
}
.addr-name {
  font-weight: 600;
  font-size: 13px;
  color: #212529;
}
.addr-detail {
  font-size: 12px;
  color: #6c757d;
  margin-top: 1px;
}
</style>

